import { useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook to track real user activity for the rested EXP system.
 * Only sends activity pings when user actually interacts with the page.
 */
export const useActivityTracker = () => {
  const { user } = useAuth();
  const lastActivitySentRef = useRef<number>(0);
  const hasRecentActivityRef = useRef<boolean>(false);
  const activityTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!user) return;

    const ACTIVITY_PING_INTERVAL = 5 * 60 * 1000; // Send activity ping every 5 minutes
    const ACTIVITY_DEBOUNCE = 10 * 1000; // Wait 10 seconds of inactivity before considering user idle

    // Send activity ping to server
    const sendActivityPing = async () => {
      const now = Date.now();
      
      // Only send if enough time has passed and user has recent activity
      if (now - lastActivitySentRef.current < ACTIVITY_PING_INTERVAL) {
        return;
      }

      if (!hasRecentActivityRef.current) {
        return;
      }

      try {
        await axios.post('/api/user/track-activity', {}, { withCredentials: true });
        lastActivitySentRef.current = now;
        hasRecentActivityRef.current = false; // Reset until next user interaction
      } catch (error) {
        // Silently fail - this is not critical
        console.debug('Failed to send activity ping:', error);
      }
    };

    // Track user interactions
    const handleUserActivity = () => {
      hasRecentActivityRef.current = true;

      // Clear existing timeout
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }

      // Debounce: send ping after user has been active
      activityTimeoutRef.current = setTimeout(() => {
        sendActivityPing();
      }, ACTIVITY_DEBOUNCE);
    };

    // Listen for various user interaction events
    const events = ['mousedown', 'keydown', 'touchstart', 'click', 'scroll'];
    
    events.forEach(event => {
      window.addEventListener(event, handleUserActivity, { passive: true });
    });

    // Send initial activity ping when component mounts
    hasRecentActivityRef.current = true;
    sendActivityPing();

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
      
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
    };
  }, [user]);
};
